import { openDB, type IDBPDatabase } from 'idb';
import type { ClientStorage, EntityName, PendingOp, PendingUpload } from '@machs/shared';

// IndexedDB je Benutzer und Team. Medien-Blobs bleiben bis zum Upload lokal.

const localUrls = new Map<string, string>();

/** Lokale Vorschau-URL für ein noch nicht (oder gerade) hochgeladenes Medium. */
export function localMediaUrl(mediaId: string): string | null {
  return localUrls.get(mediaId) ?? null;
}

export async function createWebStorage(userId: string, tenantId: string): Promise<ClientStorage> {
  const db: IDBPDatabase = await openDB(`machs-${tenantId}-${userId}`, 1, {
    upgrade(d) {
      d.createObjectStore('rows');
      d.createObjectStore('outbox');
      d.createObjectStore('uploads');
      d.createObjectStore('meta');
    },
  });

  return {
    async load() {
      const tx = db.transaction(['rows', 'outbox', 'uploads', 'meta']);
      const [rowKeys, rowVals, outbox, uploads, metaKeys, metaVals] = await Promise.all([
        tx.objectStore('rows').getAllKeys(),
        tx.objectStore('rows').getAll(),
        tx.objectStore('outbox').getAll(),
        tx.objectStore('uploads').getAll(),
        tx.objectStore('meta').getAllKeys(),
        tx.objectStore('meta').getAll(),
      ]);
      const rows = rowKeys.map((k, i) => ({ entity: String(k).split('|')[0] as EntityName, data: rowVals[i] }));
      for (const u of uploads as PendingUpload[]) {
        if (u.file instanceof Blob && !localUrls.has(u.mediaId)) localUrls.set(u.mediaId, URL.createObjectURL(u.file));
      }
      const meta = Object.fromEntries(metaKeys.map((k, i) => [String(k), String(metaVals[i])]));
      return { rows, outbox: outbox as PendingOp[], uploads: uploads as PendingUpload[], meta };
    },
    async putRows(rows) {
      if (rows.length === 0) return;
      const tx = db.transaction('rows', 'readwrite');
      for (const r of rows) {
        const key = `${r.entity}|${r.id}`;
        if (r.data) void tx.store.put(r.data, key);
        else void tx.store.delete(key);
      }
      await tx.done;
    },
    async putOutbox(op) {
      await db.put('outbox', op, op.op.opId);
    },
    async removeOutbox(ids) {
      const tx = db.transaction('outbox', 'readwrite');
      for (const id of ids) void tx.store.delete(id);
      await tx.done;
    },
    async putUpload(u) {
      if (u.file instanceof Blob) localUrls.set(u.mediaId, URL.createObjectURL(u.file));
      await db.put('uploads', u, u.mediaId);
    },
    async removeUpload(mediaId) {
      // Die lokale URL bleibt für die laufende Sitzung gültig (kein Flackern)
      await db.delete('uploads', mediaId);
    },
    async setMeta(key, value) {
      await db.put('meta', value, key);
    },
  };
}
